import { Suspense, lazy } from 'react';
import { Link } from 'react-router';
import { Card } from '../../components/ui/Card.js';
import { TripsCard } from './TripsCard.js';
import {
  type ChangeTone,
  averageRental,
  changeLabel,
  chartSeries,
  chartSummary,
  monthName,
  ringgit,
} from './dashboardSeries.js';
import type { MonthSummary } from './types.js';

/**
 * 355 (US2, design 2a): This month. Revenue is paid bookings by pickup date, as in the Sales Statement and Revenue by
 * Car, so the three pages agree (SC-001). The change compares what was picked up so far with the same days of last
 * month; bookings paid for later this month are shown apart, as booked ahead (FR-016).
 */
const DailyRevenueChart = lazy(() => import('./DailyRevenueChart.js'));

const TONE: Record<ChangeTone, string> = {
  up: 'text-semantic-success-text',
  down: 'text-semantic-danger-text',
  flat: 'text-steel',
  new: 'text-primary',
};

function Change({ current, previous }: { current: number; previous: number }) {
  const c = changeLabel(current, previous);
  return <b className={`font-semibold ${TONE[c.tone]}`}>{c.text}</b>;
}

function Figure({
  id,
  label,
  value,
  children,
}: { id: string; label: string; value: string; children?: React.ReactNode }) {
  return (
    <div
      data-testid={`month-figure-${id}`}
      className="flex min-w-0 flex-col gap-1 border-hairline-soft p-4 not-first:border-t sm:not-first:border-t-0 sm:not-first:border-l"
    >
      <span className="text-[11px] font-semibold tracking-wider text-steel uppercase">{label}</span>
      <span className="text-[21px] font-bold whitespace-nowrap text-ink tabular-nums">{value}</span>
      {children ? (
        <span className="flex flex-col gap-px text-xs text-steel">{children}</span>
      ) : null}
    </div>
  );
}

export function MonthPanel({ data, today }: { data: MonthSummary; today: string }) {
  const platform = data.scope === 'platform';
  const empty = data.revenue_minor === 0;
  const ahead = data.revenue_minor - data.revenue_so_far_minor;
  const comparedWith = `vs 1–${data.day_of_month} of last month`;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[11px] font-semibold tracking-wider text-steel uppercase">
          This month · {monthName(data.month)}
        </span>
        <span className="text-xs text-steel">
          Paid bookings by pickup date, as in the Sales Statement
        </span>
      </div>

      {/* 424: a host reads the month's money in "Your money" (the statement's figures); this panel repeats none of it. */}
      <Card padding="none" className={`grid grid-cols-2 ${platform ? 'sm:grid-cols-4' : ''}`}>
        {platform ? (
          <Figure id="revenue" label="Revenue" value={ringgit(data.revenue_minor)}>
            <span>
              {ringgit(data.revenue_so_far_minor)} so far{' '}
              <Change
                current={data.revenue_so_far_minor}
                previous={data.revenue_prev_so_far_minor}
              />
            </span>
            <span>{ringgit(ahead)} booked ahead</span>
          </Figure>
        ) : null}
        <Figure id="rentals" label="Rentals" value={String(data.rentals)}>
          <span>
            {data.rentals_so_far} so far{' '}
            <Change current={data.rentals_so_far} previous={data.rentals_prev_so_far} />
          </span>
          <span>{comparedWith}</span>
        </Figure>
        {platform ? (
          <Figure
            id="average"
            label="Avg rental"
            value={averageRental(data.revenue_minor, data.rentals)}
          >
            <span>revenue ÷ rentals</span>
          </Figure>
        ) : null}
        <Figure
          id="busy"
          label="Fleet busy"
          value={data.fleet_busy === null ? '—' : `${Math.round(data.fleet_busy * 100)}%`}
        >
          <span>
            of car-hours, {data.fleet_cars} {data.fleet_cars === 1 ? 'car' : 'cars'}
          </span>
        </Figure>
      </Card>

      <Card padding="none" className="px-4 pt-4 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-semibold text-ink">Daily revenue</span>
          <span className="flex flex-wrap gap-3.5 text-xs text-steel" aria-hidden="true">
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" />
              Picked up
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-2.5 w-2.5 rounded-sm bg-chart-ahead" />
              Booked ahead
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-0.5 w-3 bg-brand-navy opacity-40" />
              Last month, same day
            </span>
          </span>
        </div>
        <p data-testid="chart-summary" className="sr-only">
          {chartSummary(data.daily)}
        </p>
        {empty ? (
          <p data-testid="month-empty" className="py-10 text-center text-sm text-steel">
            No paid rentals yet this month.
          </p>
        ) : (
          <div className="mt-3 h-[180px]" aria-hidden="true">
            <Suspense fallback={<div className="h-[180px]" />}>
              <DailyRevenueChart points={chartSeries(data.daily, today)} />
            </Suspense>
          </div>
        )}
      </Card>

      <div
        className={`grid gap-4 ${platform ? 'lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]' : ''}`}
      >
        {empty || !platform ? null : (
          <Card padding="none" className="pt-3.5 pb-1.5" data-testid="top-cars">
            <h3 className="mx-4 mb-2 flex items-baseline justify-between text-sm font-semibold text-ink">
              Top cars
              <Link
                to="/revenue-by-car"
                className="text-sm font-medium text-primary hover:underline"
              >
                Revenue by Car →
              </Link>
            </h3>
            {data.top_cars.map((t) => (
              <div
                key={t.car_id}
                className="grid grid-cols-[76px_minmax(0,1fr)_48px_76px] items-center gap-2.5 border-t border-hairline-soft px-4 py-2 text-sm"
              >
                <b className="font-semibold text-ink">{t.plate ?? '—'}</b>
                <span className="truncate text-slate">{t.model}</span>
                <span className="text-right text-steel tabular-nums">
                  {data.revenue_minor > 0
                    ? `${((t.revenue_minor / data.revenue_minor) * 100).toFixed(1)}%`
                    : ''}
                </span>
                <span className="text-right font-semibold text-ink tabular-nums">
                  {ringgit(t.revenue_minor)}
                </span>
              </div>
            ))}
            {platform ? null : (
              <div className="px-4 pt-2.5 pb-1.5">
                <Link
                  to="/sales-statement"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Sales Statement →
                </Link>
              </div>
            )}
          </Card>
        )}
        {platform ? (
          <Card padding="none" className="pt-3.5 pb-1.5" data-testid="by-campus">
            <h3 className="mx-4 mb-2 text-sm font-semibold text-ink">By campus</h3>
            {(data.by_campus ?? []).map((c) => (
              <div
                key={c.location_id || 'none'}
                className="flex flex-col gap-1.5 border-t border-hairline-soft px-4 py-2.5 text-sm"
              >
                <div className="flex justify-between gap-2">
                  <b className="font-semibold text-ink">{c.name}</b>
                  <span className="tabular-nums text-ink">{ringgit(c.revenue_minor)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-hairline-soft">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{
                      width: `${data.revenue_minor > 0 ? (c.revenue_minor / data.revenue_minor) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-xs text-steel">
                  {c.rentals} {c.rentals === 1 ? 'rental' : 'rentals'}
                </span>
              </div>
            ))}
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-4 pt-2.5 pb-1.5">
              <Link
                to="/sales-statement"
                className="text-sm font-medium whitespace-nowrap text-primary hover:underline"
              >
                Sales Statement →
              </Link>
              <Link
                to="/finance"
                className="text-sm font-medium whitespace-nowrap text-primary hover:underline"
              >
                Finance →
              </Link>
            </div>
          </Card>
        ) : null}
      </div>
      {/* 369 (#1237, design B): the platform's month by supplier — the same revenue each Sales Statement shows. */}
      {platform && data.by_supplier && data.by_supplier.length > 0 ? (
        <Card padding="none" className="pt-3.5 pb-1.5" data-testid="by-supplier">
          <h3 className="mx-4 mb-2 flex items-baseline justify-between text-sm font-semibold text-ink">
            By supplier
            <Link
              to="/sales-statement"
              className="text-sm font-medium text-primary hover:underline"
            >
              Sales Statement →
            </Link>
          </h3>
          <div className="overflow-x-auto px-4">
            <table aria-label="By supplier" className="w-full text-sm">
              <thead>
                <tr className="text-xs text-steel">
                  <th scope="col" className="py-1.5 pr-3 text-left font-medium">
                    Supplier
                  </th>
                  <th scope="col" className="py-1.5 pr-3 text-right font-medium">
                    Rentals
                  </th>
                  <th scope="col" className="py-1.5 pr-3 text-right font-medium">
                    Revenue
                  </th>
                  <th scope="col" className="w-[30%] py-1.5">
                    <span className="sr-only">Share of the month</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.by_supplier.map((s) => (
                  <tr key={s.supplier_id || 'none'} className="border-t border-hairline-soft">
                    <th scope="row" className="py-2 pr-3 text-left font-semibold text-ink">
                      {s.name}
                    </th>
                    <td className="py-2 pr-3 text-right text-ink tabular-nums">{s.rentals}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-ink tabular-nums">
                      {ringgit(s.revenue_minor)}
                    </td>
                    <td aria-hidden="true" className="py-2">
                      <div className="h-1.5 rounded-full bg-hairline-soft">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{
                            width: `${data.revenue_minor > 0 ? (s.revenue_minor / data.revenue_minor) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
      {/* 361 (#980, design 1h): the month's rentals by the customers' trip answers, and the CSV export. */}
      {data.trips ? <TripsCard trips={data.trips} month={data.month} /> : null}
    </div>
  );
}
